import { Fragment, ReactNode, useState } from 'react'
import { ChevronDown } from '@digital-twins/design-system/icons'
import { MarkdownBody } from '@design-system/components/MarkdownBody'
import { WorkflowRunStep, localizedWorkflowStepTitle } from '@digital-twins/shared-types'

import { ArtifactChromeActions } from '@/components/artifact-chrome/ArtifactChromeActions'
import { ArtifactEditorShell } from '@/components/artifact-editor/ArtifactEditorShell'
import { artifactPeekClass } from '@/components/artifact-editor/artifactPeek'
import { reconcileArtifactBody } from '@/components/artifact-editor/reconcileArtifactBody'
import {
  hasStudyDocEmbeds,
  StudyDocBody,
  StudyDocPlotLookup,
  StudyDocPrintButton,
  StudyDocQuote,
} from '@/components/study-doc'

import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'

import { useArtifactStudyDoc } from '../useArtifactStudyDoc'
import { ArtifactTipView } from '@/lib/api'
import { cn } from '@/lib/utils'

import {
  artifactDisplayLabel,
  bodyOpensWithHeading,
  localizeArtifactCategory,
  stripLeadingHeading,
} from './artifactDisplayLabel'
import { STUDY_DOC_SLUG, ThreadArtifactItem } from './collectThreadArtifacts'
import { filterInlineThreadArtifacts } from './filterInlineThreadArtifacts'
import { groupArtifactsByStep } from './groupArtifactsByStep'
import { ThreadStepGroup } from './ThreadStepGroup'
import { useThreadArtifactTips } from './useThreadArtifactTips'

/**
 * Shared preview surface for a written workflow artifact.
 *
 * Title row (the body's opening heading + copy + expand) over a markdown
 * body. The category ("Desk research", "Brief") is not shown on the card:
 * the step group header above it already says what kind of output this is,
 * so the card carries the artifact's own title and nothing else. The thread
 * column mounts this as an embed: body open, document density, peek-capped.
 * `collapsedByDefault` remains for hosts that still start as a title row.
 */

export interface ArtifactPreviewCardProps {
  item: ThreadArtifactItem
  /** Opens the full artifact (e.g. a deep link to its producing run/step). Hidden when omitted. */
  onExpand?: (item: ThreadArtifactItem) => void
  /**
   * ChatGPT-parity Edit. Hidden unless provided so existing cards stay
   * Copy + Expand.
   */
  onEdit?: (item: ThreadArtifactItem) => void
  /** Owner-only; Edit stays hidden when false even if `onEdit` is set. */
  canEdit?: boolean
  /**
   * Tip `editedBody` wins over machine `item.body` when present for `item.key`.
   */
  tip?: ArtifactTipView | null
  /**
   * THE LIVING lineage. When omitted, derived from `tip.original`.
   * Findings tips pass `original={false}` after an extend.
   */
  original?: boolean
  /**
   * Hides the title row. A host that already renders the title elsewhere
   * (e.g. the thread rail's own chevron header) sets this to false rather
   * than showing it twice; the preview body is still the full visual.
   */
  showHeader?: boolean
  /**
   * Clamps the body to a few lines (the "peek"). A host that has already
   * committed to showing this one artifact in full — the rail's own
   * expand/collapse toggle, an inline card the reader opened — sets this to
   * false so the same preview surface renders the whole body instead of
   * requiring a second, separate "full text" component.
   */
  clamp?: boolean
  /**
   * Starts with the body hidden. The title row toggles the body inline;
   * the Maximize `onExpand` path is unchanged.
   */
  collapsedByDefault?: boolean
  /**
   * Caps an unclamped body (ChatGPT-style peek). Expand opens the rest.
   */
  peek?: boolean
  /**
   * Plot lookup for `{{plot:id}}` tokens. Without this, StudyDoc embeds
   * render as placeholders.
   */
  plots?: Record<string, StudyDocPlotLookup>
  quotes?: Record<string, StudyDocQuote>
  plotRegion?: 'all' | 'plot'
  /** Slim picker population for mosaic tiles missing a row image. */
  populationId?: string
  beforePlotBand?: ReactNode
  /** Extra title-row action, such as the StudyDoc print control. */
  headerExtra?: ReactNode
  /**
   * Replaces the default markdown / StudyDoc body. Chrome (Edit · Copy ·
   * Expand) stays. Thread twin-test uses this so cut picker + plots stay
   * inside the artifact.
   */
  children?: ReactNode
  className?: string
}

export function ArtifactPreviewCard(props: ArtifactPreviewCardProps) {
  const {
    item,
    onExpand,
    onEdit,
    canEdit = true,
    tip = null,
    original,
    showHeader = true,
    clamp = true,
    collapsedByDefault = false,
    peek = false,
    plots,
    quotes,
    plotRegion,
    populationId,
    beforePlotBand,
    headerExtra,
    children,
    className,
  } = props

  /* Lookups the caller did not supply, resolved from the artifact's own run.
     A host that passes `plots` (the page's fullscreen and expanded views)
     keeps winning; a host that passes nothing (the inline stack, the rail)
     used to render placeholder boxes over a body whose values were one fetch
     away. */
  const ownStudyDoc = useArtifactStudyDoc(plots ? null : item)
  const resolvedPlots = plots ?? ownStudyDoc?.plots
  const resolvedQuotes = quotes ?? ownStudyDoc?.quotes
  const resolvedPopulationId = populationId ?? ownStudyDoc?.populationId

  const [isBodyOpen, setIsBodyOpen] = useState(!collapsedByDefault)
  const [hasCopied, setHasCopied] = useState(false)
  const displayBody = reconcileArtifactBody(item.body, tip)
  const isOriginal = original ?? tip?.original ?? true
  const label = artifactDisplayLabel({ ...item, body: displayBody })
  // The chrome headline is that first heading; the body must not repeat it.
  const bodyMarkdown = showHeader ? stripLeadingHeading(displayBody) : displayBody
  const categoryText = localizeArtifactCategory(label.category, label.letter)
  const accessibleName = categoryText
    ? `${categoryText}: ${label.headline}`
    : label.headline

  function handleExpandClick() {
    if (onExpand) {
      onExpand(item)
    }
  }

  function handleEditClick() {
    if (onEdit) {
      onEdit(item)
    }
  }

  async function handleCopyClick() {
    if (!navigator.clipboard?.writeText) {
      return
    }
    try {
      await navigator.clipboard.writeText(displayBody)
      setHasCopied(true)
      window.setTimeout(() => setHasCopied(false), 1500)
    } catch {
      setHasCopied(false)
    }
  }

  function handleToggleBody() {
    setIsBodyOpen((current) => !current)
  }

  const chromeActions = (
    <ArtifactChromeActions
      accessibleName={accessibleName}
      hasCopied={hasCopied}
      canEdit={canEdit}
      onCopy={handleCopyClick}
      onExpand={onExpand ? handleExpandClick : undefined}
      onEdit={onEdit ? handleEditClick : undefined}
      testIdPrefix="artifact-preview-card"
    />
  )

  /* The visible title is the body's own opening heading, shown once above
     the body (`bodyMarkdown` has it stripped). A body that opens with prose
     has no title row; its first line is already the opening. */
  const hasHeading = bodyOpensWithHeading(displayBody)
  /* A lettered set member ("Creative A") is the one card that needs its own
     label: the step group above names the set, not the version, and the
     versions sit side by side. Every other card leaves the type to the
     group. */
  const setLabel = label.letter ? categoryText : null
  const hasVisibleTitle = hasHeading || setLabel !== null

  const titleHeading = hasVisibleTitle ? (
    <div className="min-w-0 flex-1">
      {setLabel ? (
        <Text
          as="span"
          size="xs"
          weight="medium"
          className={cn('block text-muted-foreground', hasHeading ? 'mb-1' : undefined)}
          data-testid="artifact-preview-card-set-label"
        >
          {setLabel}
        </Text>
      ) : null}
      {hasHeading ? (
        <Heading
          level={3}
          size="lg"
          weight="medium"
          className="leading-snug"
          data-testid="artifact-preview-card-title"
        >
          {label.headline}
        </Heading>
      ) : null}
    </div>
  ) : null

  const header = showHeader ? (
    <div
      className={cn(
        'flex gap-2',
        !collapsedByDefault && hasVisibleTitle ? 'items-start' : 'items-center',
        !collapsedByDefault && !hasVisibleTitle ? 'justify-end' : undefined,
      )}
    >
      {collapsedByDefault ? (
        <button
          type="button"
          className={cn(
            'group flex min-w-0 flex-1 cursor-pointer gap-2 text-left',
            isBodyOpen ? 'items-start' : 'items-center',
          )}
          data-testid="artifact-preview-card-toggle"
          aria-expanded={isBodyOpen}
          onClick={handleToggleBody}
        >
          {isBodyOpen ? (
            /* Open, the row is the document's own title, same as every
               other card: full heading, wrapping. Closed, it is a one-line
               row the reader can scan past. */
            <Heading
              level={3}
              size="lg"
              weight="medium"
              className="min-w-0 flex-1 leading-snug"
              data-testid="artifact-preview-card-title"
            >
              {label.headline}
            </Heading>
          ) : (
            <Text
              as="span"
              size="sm"
              weight="medium"
              className="min-w-0 flex-1 truncate"
              data-testid="artifact-preview-card-title"
            >
              {label.headline}
            </Text>
          )}
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-[transform,color] group-hover:text-foreground',
              isBodyOpen ? 'rotate-180' : '',
            )}
            aria-hidden
          />
        </button>
      ) : (
        titleHeading
      )}
      {isOriginal === false ? (
        <span
          className="shrink-0 rounded border border-border/70 bg-muted/50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
          data-testid="artifact-lineage-badge"
        >
          Derived
        </span>
      ) : null}
      <div className="flex shrink-0 items-center gap-1">
        {headerExtra}
        {chromeActions}
      </div>
    </div>
  ) : (
    <div className="flex items-center justify-end gap-2">
      {isOriginal === false ? (
        <span
          className="rounded border border-border/70 bg-muted/50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
          data-testid="artifact-lineage-badge"
        >
          Derived
        </span>
      ) : null}
      <div className="flex shrink-0 items-center gap-1">
        {headerExtra}
        {chromeActions}
      </div>
    </div>
  )

  const preview = isBodyOpen ? (
    <div
      className={cn(
        clamp ? 'line-clamp-3' : undefined,
        peek ? artifactPeekClass(displayBody) : undefined,
        showHeader ? 'mt-2' : undefined,
      )}
      data-testid="artifact-preview-card-preview"
    >
      {children ? (
        children
      ) : hasStudyDocEmbeds(bodyMarkdown) || resolvedPlots ? (
        <StudyDocBody
          markdown={bodyMarkdown}
          layout="lite-magazine"
          plots={resolvedPlots}
          quotes={resolvedQuotes}
          plotRegion={plotRegion}
          populationId={resolvedPopulationId}
          beforePlotBand={beforePlotBand}
        />
      ) : (
        <MarkdownBody content={bodyMarkdown} density="thread" />
      )}
      {peek ? (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-muted/30 to-transparent"
          aria-hidden
        />
      ) : null}
    </div>
  ) : null

  return (
    <div className={className} data-testid="artifact-preview-card">
      {header}
      {preview}
    </div>
  )
}

export type ThreadStepNotePlacement = 'before' | 'after'

export interface ThreadStepNoteNode {
  key: string
  stepIndex: number
  /**
   * `before`: render immediately before the group whose stepIndex matches
   * (handoff narration). `after` (default): render after the nearest group
   * whose stepIndex <= the note's (gate decisions, and the fallback for a
   * `before` note whose step produced no group).
   */
  placement?: ThreadStepNotePlacement
  /** Revision attempt of a handoff note; 0 or absent for the first attempt. */
  attempt?: number
  node: ReactNode
}

export interface ThreadInlineArtifactsProps {
  items: Array<ThreadArtifactItem>
  onExpand: (item: ThreadArtifactItem) => void
  /** Thread token for living `updateArtifact` writes. */
  threadToken?: string | null
  /**
   * The run's steps, for grouping cards under the step that produced them.
   * Omitted or empty keeps the flat stack, which is what a thread with no run
   * and a legacy run without step attribution both want.
   */
  steps?: Array<WorkflowRunStep>
  /** Seeded template slug for group-header overlay. */
  templateSlug?: string | null
  /** Thread / answer language for seeded group titles. */
  language?: string | null
  /**
   * The run's step and gate lines. A `before` note renders immediately before
   * the group with its stepIndex; an `after` note (the default) renders after
   * the group of the step it follows (largest group stepIndex <=
   * note.stepIndex; before the first group when none). `node` is the
   * already-rendered turn so this component stays ignorant of turn rendering.
   */
  stepNotes?: Array<ThreadStepNoteNode>
}

export interface BucketedStepNotes<T> {
  /** Notes with no preceding group at all: rendered above every group. */
  before: Array<T>
  /** `placement: 'before'` notes keyed by the GROUP POSITION they precede. */
  beforeGroup: Map<number, Array<T>>
  /** Keyed by the note's anchor GROUP POSITION (index into `groups`), not stepIndex. */
  after: Map<number, Array<T>>
}

function pushBucket<T>(map: Map<number, Array<T>>, position: number, note: T) {
  const bucket = map.get(position)
  if (bucket) {
    bucket.push(note)
    return
  }
  map.set(position, [note])
}

/**
 * Keeps only the highest-attempt `before` note per step index (Step 6 of
 * `docs/plans/2026-09-05-revise-step-under-review-from-chat.md`, D9): an
 * earlier attempt's handoff describes a document a revision replaced, so
 * showing both would narrate a body that no longer exists. `after` notes
 * (gate decisions) and notes with no `attempt` (treated as 0, so a run with
 * no revisions never loses anything) pass through unchanged. Preserves the
 * relative order of the notes that survive.
 */
function dedupeBeforeNotesByHighestAttempt<
  T extends { stepIndex: number; placement?: ThreadStepNotePlacement; attempt?: number },
>(notes: Array<T>): Array<T> {
  const bestBeforeByStepIndex = new Map<number, T>()
  notes.forEach((note) => {
    if (note.placement !== 'before') {
      return
    }
    const existing = bestBeforeByStepIndex.get(note.stepIndex)
    if (!existing || (note.attempt ?? 0) > (existing.attempt ?? 0)) {
      bestBeforeByStepIndex.set(note.stepIndex, note)
    }
  })
  return notes.filter(
    (note) =>
      note.placement !== 'before' ||
      bestBeforeByStepIndex.get(note.stepIndex) === note,
  )
}

/**
 * Buckets step/gate notes against the rendered group positions.
 *
 * A `placement: 'before'` note (handoff narration) goes into `beforeGroup`
 * at the position of the group whose stepIndex equals the note's, so the
 * reader gets the narrative and then the step header with its cards. When
 * several `before` notes share a step index (one per revision attempt), only
 * the highest-attempt one survives (see `dedupeBeforeNotesByHighestAttempt`).
 *
 * Every other note — `after` placement, or a `before` note whose step
 * produced no group — anchors on the LAST group position whose stepIndex is
 * <= the note's own stepIndex and lands right after it. That is how a note
 * for a step with no card (a Slack delivery step, a gate) attaches to the
 * nearest preceding group instead of being dropped.
 *
 * `groupStepIndexes[i]` is the stepIndex of `groups[i]`, or `null` when that
 * group has no known stepIndex (the trailing unattributed bucket from
 * `groupArtifactsByStep`). A `null` entry never wins as an anchor, so notes
 * never attach to that catch-all group.
 */
export function bucketStepNotes<
  T extends { stepIndex: number; placement?: ThreadStepNotePlacement; attempt?: number },
>(
  notes: Array<T>,
  groupStepIndexes: Array<number | null>,
): BucketedStepNotes<T> {
  const before: Array<T> = []
  const beforeGroup = new Map<number, Array<T>>()
  const after = new Map<number, Array<T>>()

  dedupeBeforeNotesByHighestAttempt(notes).forEach((note) => {
    if (note.placement === 'before') {
      const ownPosition = groupStepIndexes.findIndex(
        (stepIndex) => stepIndex !== null && stepIndex === note.stepIndex,
      )
      if (ownPosition !== -1) {
        pushBucket(beforeGroup, ownPosition, note)
        return
      }
    }

    let anchorPosition = -1
    groupStepIndexes.forEach((stepIndex, position) => {
      if (stepIndex !== null && stepIndex <= note.stepIndex) {
        anchorPosition = position
      }
    })
    if (anchorPosition === -1) {
      before.push(note)
      return
    }
    pushBucket(after, anchorPosition, note)
  })

  return { before, beforeGroup, after }
}

/**
 * A brief is the working material the report was built from, not the
 * deliverable. Inline it renders as a one-line row the reader can open.
 */
export function isCollapsedInlineArtifact(item: ThreadArtifactItem): boolean {
  return item.kind === 'brief'
}

/**
 * Deliverables first, briefs after. The API hands artifacts newest-first, so
 * the report already lands above the brief it grew from; this pins that
 * order so a later tip edit to the brief (which bumps its `updatedAt`) cannot
 * float it back above the report. Stable within each group.
 *
 * FLAT PATH ONLY. Once a run's cards are grouped by step, ordering is the
 * run's own order and this ranking no longer applies: the brief comes before
 * the report because the step that wrote it ran first, and the reader lands
 * at the end of the thread where the deliverable is. This still orders a
 * thread with no run to group by, so it is not dead.
 */
export function orderInlineThreadArtifacts(
  items: Array<ThreadArtifactItem>,
): Array<ThreadArtifactItem> {
  const leading = items.filter((item) => !isCollapsedInlineArtifact(item))
  const trailing = items.filter((item) => isCollapsedInlineArtifact(item))
  return [...leading, ...trailing]
}

/**
 * Thread-column stack: questions payloads are omitted; remaining cards
 * render as markdown embeds with copy, expand, and owner edit (tip autosave).
 */
export function ThreadInlineArtifacts(props: ThreadInlineArtifactsProps) {
  const {
    items,
    onExpand,
    threadToken = null,
    steps = [],
    templateSlug,
    language,
    stepNotes = [],
  } = props

  const [editingKey, setEditingKey] = useState<string | null>(null)
  const runToken = items.find((item) => item.runToken)?.runToken ?? null
  const { tipForItem, canEdit: tipCanEdit, upsertLocalTip } =
    useThreadArtifactTips(runToken)

  const visibleItems = orderInlineThreadArtifacts(
    filterInlineThreadArtifacts(items),
  )

  if (visibleItems.length === 0 && stepNotes.length === 0) {
    return null
  }

  const orderedNotes = [...stepNotes].sort(
    (left, right) => left.stepIndex - right.stepIndex,
  )

  if (visibleItems.length === 0) {
    return (
      <div className="mt-2 space-y-2" data-testid="thread-inline-artifacts">
        {orderedNotes.map(renderNote)}
      </div>
    )
  }

  /* Grouping needs both a step list and artifacts attributed to it. Either
     one missing keeps the flat stack rather than rendering a lone untitled
     group, which would be chrome with no information inside it. */
  const groups = groupArtifactsByStep(visibleItems, steps)
  const isGrouped = groups.some((group) => group.title !== null)
  const stepKeyById = new Map(steps.map((step) => [step.stepId, step.stepKey]))

  if (!isGrouped) {
    return (
      <div className="mt-2 space-y-2" data-testid="thread-inline-artifacts">
        {visibleItems.map(renderItem)}
        {orderedNotes.map(renderNote)}
      </div>
    )
  }

  const stepIndexById = new Map(
    steps.map((step) => [step.stepId, step.stepIndex]),
  )
  const groupStepIndexes = groups.map(
    (group) => stepIndexById.get(group.key) ?? null,
  )
  const { before, beforeGroup, after } = bucketStepNotes(
    stepNotes,
    groupStepIndexes,
  )

  return (
    <div className="mt-2 space-y-5" data-testid="thread-inline-artifacts">
      {before.map(renderNote)}
      {groups.map((group, position) => (
        <Fragment key={group.key || 'ungrouped'}>
          {(beforeGroup.get(position) ?? []).map(renderNote)}
          {group.title === null || group.state === null ? (
            <div
              className="space-y-2"
              data-testid="thread-step-group-unattributed"
            >
              {group.items.map(renderItem)}
            </div>
          ) : (
            <ThreadStepGroup
              title={localizedWorkflowStepTitle({
                templateSlug,
                stepKey: stepKeyById.get(group.key),
                language,
                fallback: group.title,
              })}
              state={group.state}
              at={group.at}
              stepId={group.key}
            >
              <div className="space-y-2">{group.items.map(renderItem)}</div>
            </ThreadStepGroup>
          )}
          {(after.get(position) ?? []).map(renderNote)}
        </Fragment>
      ))}
    </div>
  )

  function renderNote(note: ThreadStepNoteNode) {
    return (
      <div
        key={note.key}
        data-testid="thread-step-note"
        data-step-index={note.stepIndex}
        data-placement={note.placement ?? 'after'}
        className="space-y-3"
      >
        {note.node}
      </div>
    )
  }

  function renderItem(item: ThreadArtifactItem) {
    const tip = tipForItem(item)
    const displayBody = reconcileArtifactBody(item.body, tip)
    const label = artifactDisplayLabel({ ...item, body: displayBody })
    const categoryText = localizeArtifactCategory(
      label.category,
      label.letter,
    )
    const accessibleName = categoryText
      ? `${categoryText}: ${label.headline}`
      : label.headline
    const tipIdentity = item.publicId ?? item.key
    const isFindingsTip =
      item.kind === 'findings' ||
      item.slug === 'findings' ||
      Boolean(tip?.artifactIdentity?.includes(':findings:'))
    const livingCanEdit = Boolean(
      !item.runToken && threadToken && item.publicId,
    )
    // Findings numbers are read-only — never tip-edit stats.
    const allowEdit = (tipCanEdit || livingCanEdit) && !isFindingsTip
    const isEditing = allowEdit && editingKey === tipIdentity

    if (isEditing) {
      return (
        <ArtifactEditorShell
          key={item.key}
          value={displayBody}
          accessibleName={accessibleName}
          canEdit={allowEdit}
          isEditing
          peek={false}
          original={tip?.original ?? true}
          onExpand={() => onExpand(item)}
          onDone={() => setEditingKey(null)}
          onCancel={() => setEditingKey(null)}
          autosave={{
            runToken: item.runToken ?? '',
            stepId: tip?.stepId || item.stepId || 'living',
            artifactIdentity: tipIdentity,
            machineBody: item.body,
            stepIndex: item.stepIndex,
            existingTip: tip,
            onSaved: upsertLocalTip,
            threadToken,
            livingPublicId: item.publicId ?? null,
            livingVersion: item.version ?? tip?.version ?? null,
          }}
          className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3"
          testIdPrefix="artifact-preview-card"
        />
      )
    }

    return (
      <ArtifactPreviewCard
        key={item.key}
        item={item}
        tip={tip}
        original={tip?.original ?? true}
        canEdit={allowEdit}
        clamp={false}
        peek
        collapsedByDefault={isCollapsedInlineArtifact(item)}
        onExpand={onExpand}
        headerExtra={
          item.slug === STUDY_DOC_SLUG ? (
            <StudyDocPrintButton
              accessibleName={accessibleName}
              documentTitle={item.title || accessibleName}
            />
          ) : undefined
        }
        onEdit={
          allowEdit
            ? () => {
                setEditingKey(tipIdentity)
              }
            : undefined
        }
        className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3"
      />
    )
  }
}
